import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import SupportModeBanner from './SupportModeBanner';
import AlertBell from './AlertBell';
import AssistantPanel from './AssistantPanel';
import { useI18n, LanguageSwitcher, type TranslationKey } from '../i18n';
import { ThemeSwitcher } from '../theme';
import { useLiveStats } from '../hooks/useLiveStats';
import { formatPower } from '../utils/power';

// Icônes des onglets (trait, style Heroicons)
const ICONS = {
  home: 'M4 13h6V4H4v9Zm0 7h6v-4H4v4Zm10 0h6v-9h-6v9Zm0-16v4h6V4h-6Z',
  equipment: 'M9 3v2M15 3v2M9 19v2M15 19v2M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z',
  architecture: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6',
  ota: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  chargers: 'M7 22V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v18M5 22h14M10 8l-1 3h4l-1 3',
  pairing: 'M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 19.5h.01',
  installers: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5Z',
  members: 'M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM22 19v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8',
  models: 'M9 3v2M15 3v2M9 19v2M15 19v2M3 9h2M3 15h2M19 9h2M19 15h2M7 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2ZM9 9h6v6H9z',
  clients: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6',
  organisation: 'M3 21h18M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M15 21V9h4a1 1 0 0 1 1 1v11M9 7h2M9 11h2M9 15h2',
};

type Tone = 'blue' | 'green' | 'amber' | 'slate';
const CHIP: Record<Tone, string> = { blue: 'bg-blue-50 text-blue-700', green: 'bg-emerald-50 text-emerald-700', amber: 'bg-amber-50 text-amber-700', slate: 'bg-slate-100 text-slate-600' };
const DOT: Record<Tone, string> = { blue: 'bg-blue-500', green: 'bg-emerald-500', amber: 'bg-amber-500', slate: 'bg-slate-400' };

interface NavEntry { to: string; label: string; icon: string; end?: boolean; chip?: string; chipTitle?: string; tone?: Tone }

const Icon = ({ d, className = 'w-4 h-4 shrink-0' }: { d: string; className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={d} /></svg>
);

/** Compteur en direct d'un onglet (puissance, appareils en ligne, bornes…), avec point de couleur. */
function NavChip({ chip, chipTitle, tone = 'slate', active }: { chip: string; chipTitle?: string; tone?: Tone; active: boolean }) {
  return (
    <span title={chipTitle} className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-full font-mono text-[11px] font-semibold tabular-nums ${active ? 'bg-white/15 text-white' : CHIP[tone]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white/80' : DOT[tone]}`} aria-hidden />
      {chip}
      {chipTitle && <span className="sr-only"> · {chipTitle}</span>}
    </span>
  );
}

function isCurrent(pathname: string, it: NavEntry): boolean {
  return it.end ? pathname === it.to : pathname === it.to || pathname.startsWith(`${it.to}/`);
}

/**
 * Menu compact (téléphones, < 640 px). La barre n'a pas la place des libellés et le survol n'existe pas
 * au tactile, donc un bouton affiche la page courante et ouvre un panneau latéral listant toutes les pages
 * avec icône, libellé et compteur en direct.
 */
function CompactNav({ items, groupLabel, groupIcon, groupItems, footer }: { items: NavEntry[]; groupLabel: string; groupIcon: string; groupItems: NavEntry[]; footer?: ReactNode }) {
  const { t } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const current = [...items, ...groupItems].find(it => isCurrent(location.pathname, it));

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [open]);

  const link = (it: NavEntry) => (
    <NavLink
      key={it.to}
      to={it.to}
      end={it.end}
      className={({ isActive }) =>
        `flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive ? 'bg-zce-navy text-white shadow-sm' : 'text-gray-700 hover:bg-gray-100 hover:text-zce-navy'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon d={it.icon} className="w-5 h-5 shrink-0" />
          <span className="flex-1 truncate">{it.label}</span>
          {it.chip && <NavChip chip={it.chip} chipTitle={it.chipTitle} tone={it.tone} active={isActive} />}
        </>
      )}
    </NavLink>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('nav_menu')}
        className="inline-flex items-center gap-2 h-9 px-2.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-100 hover:text-zce-navy transition-colors"
      >
        <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden><path d="M4 7h16M4 12h16M4 17h16" /></svg>
        {current && (
          <span className="hidden min-[480px]:inline-flex items-center gap-1.5 max-w-[180px]">
            <Icon d={current.icon} />
            <span className="truncate">{current.label}</span>
            {current.chip && <NavChip chip={current.chip} chipTitle={current.chipTitle} tone={current.tone} active={false} />}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[400]" role="dialog" aria-modal="true" aria-label={t('nav_menu')}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[300px] max-w-[85vw] bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
              <span className="text-xl font-bold text-primary-600">ZOS</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('nav_close')}
                title={t('nav_close')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-2 space-y-0.5">
              {items.map(link)}
              {groupItems.length > 0 && (
                <>
                  <div className="pt-5 pb-1.5 px-5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    <Icon d={groupIcon} className="w-3.5 h-3.5 shrink-0" />
                    {groupLabel}
                  </div>
                  {groupItems.map(link)}
                </>
              )}
            </nav>
            {footer && <div className="border-t border-gray-200 px-4 py-3">{footer}</div>}
          </aside>
        </div>
      )}
    </>
  );
}

/** Groupe d'onglets dans un menu déroulant (pages secondaires) : la barre reste sur une ligne. */
function NavGroup({ label, icon, items }: { label: string; icon: string; items: NavEntry[] }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = items.some(it => location.pathname === it.to || location.pathname.startsWith(`${it.to}/`));

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
          active ? 'bg-zce-navy text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-zce-navy'
        }`}
      >
        <Icon d={icon} />
        <span>{label}</span>
        <svg className={`w-3.5 h-3.5 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-[300] mt-1.5 min-w-[230px] rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl">
          {items.map(it => (
            <NavLink
              key={it.to}
              to={it.to}
              role="menuitem"
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${isActive ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              <Icon d={it.icon} />
              <span className="flex-1">{it.label}</span>
              {it.chip && <span title={it.chipTitle} className="font-mono text-[11px] font-semibold tabular-nums px-1.5 h-5 inline-flex items-center rounded-full bg-slate-100 text-slate-600">{it.chip}</span>}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/** Onglet du menu : icône, libellé et compteur en direct facultatif ; onglet actif en navy plein. */
function NavItem({ to, label, icon, end, chip, chipTitle, tone = 'slate' }: NavEntry) {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      aria-label={chipTitle ? `${label} · ${chipTitle}` : label}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive ? 'bg-zce-navy text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-zce-navy'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon d={icon} />
          <span>{label}</span>
          {chip && <NavChip chip={chip} chipTitle={chipTitle} tone={tone} active={isActive} />}
        </>
      )}
    </NavLink>
  );
}

// Libellés des rôles (org et plateforme), traduits à l'affichage.
const ROLE_LABELS: Record<string, TranslationKey> = {
  org_admin: 'team_role_org_admin',
  installer_admin: 'team_role_installer_admin',
  installer_technician: 'team_role_installer_technician',
  admin: 'sh_role_admin',
  maintenance: 'team_role_maintenance',
  user: 'team_role_user',
};

function Layout() {
  const { user, logout, hasAccess, currentMembership } = useAuth();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Rôle inconnu : valeur brute, mise en forme comme avant (capitalize).
  const roleLabel = (role: string) =>
    ROLE_LABELS[role] ? t(ROLE_LABELS[role]) : <span className="capitalize">{role.replace('_', ' ')}</span>;

  const installerRole = currentMembership?.role === 'installer_admin' || currentMembership?.role === 'installer_technician';

  // If installer is back on /my-clients (or any installer-only route), purge any stale
  // support-mode state from a previous session. Otherwise the full nav would leak.
  useEffect(() => {
    if (installerRole && (location.pathname === '/my-clients' || location.pathname.startsWith('/my-clients'))) {
      sessionStorage.removeItem('actingClientOrgId');
      sessionStorage.removeItem('actingClientName');
    }
  }, [installerRole, location.pathname]);

  // Support mode = installer currently impersonating a client.
  // Only active when we're NOT on the installer's own clients list.
  const onMyClientsPage = location.pathname === '/my-clients' || location.pathname.startsWith('/my-clients/');
  const inSupportMode =
    typeof window !== 'undefined' &&
    !!sessionStorage.getItem('actingClientOrgId') &&
    !onMyClientsPage;
  // When in support mode, treat as a normal org member (show full nav like a maintenance user)
  const isInstaller = installerRole && !inSupportMode;
  // ESLint: use location to force re-render on route change
  void location.pathname;

  // Menu : onglets et compteurs en direct (puissance, appareils en ligne, bornes en charge, membres)
  const isOrgAdmin = currentMembership?.role === 'org_admin';
  const live = useLiveStats(!isInstaller && hasAccess('wattelzce'), isOrgAdmin);
  const navItems: NavEntry[] = isInstaller ? [
    { to: '/my-clients', label: t('nav_myClients'), icon: ICONS.clients },
    ...(currentMembership?.role === 'installer_admin' ? [{ to: '/members', label: t('nav_team'), icon: ICONS.members }] : []),
  ] : [
    { to: '/', end: true, label: 'Smart Building', icon: ICONS.home, tone: 'blue',
      chip: live.powerKw != null ? formatPower(live.powerKw, locale) : undefined, chipTitle: t('nav_chip_power') },
    ...(hasAccess('wattelzce') ? [
      { to: '/wattel-em', label: t('nav_equipment'), icon: ICONS.equipment, tone: (live.online === live.total ? 'green' : 'amber') as Tone,
        chip: live.total ? `${live.online}/${live.total}` : undefined, chipTitle: t('nav_chip_online', { online: live.online ?? 0, total: live.total ?? 0 }) },
      { to: '/building-architecture', label: t('nav_architecture'), icon: ICONS.architecture },
      ...(user?.platformRole === 'super_admin' ? [{ to: '/ota', label: 'OTA', icon: ICONS.ota }] : []),
      { to: '/bornes', label: t('nav_chargers'), icon: ICONS.chargers, tone: (live.charging ? 'amber' : 'slate') as Tone,
        chip: live.charging ? String(live.charging) : live.bornes ? String(live.bornes) : undefined,
        chipTitle: live.charging ? t('nav_chip_charging', { n: live.charging }) : t('nav_chip_bornes', { n: live.bornes ?? 0 }) },
      { to: '/wifi-provisioning', label: t('nav_pairing'), icon: ICONS.pairing },
    ] : []),
  ];
  // Pages d'administration de l'organisation : regroupées dans « Organisation ▾ »
  const orgItems: NavEntry[] = isOrgAdmin && !isInstaller ? [
    { to: '/members', label: t('nav_members'), icon: ICONS.members, chip: live.members ? String(live.members) : undefined, chipTitle: t('nav_chip_members', { n: live.members ?? 0 }) },
    { to: '/my-installers', label: t('nav_myInstallers'), icon: ICONS.installers },
    { to: '/device-models', label: t('nav_deviceModels'), icon: ICONS.models },
  ] : [];

  // Identité affichée dans la barre (≥ 1536 px) et au pied du menu compact (< 640 px).
  const identity = (
    <>
      <p className="text-sm font-medium text-gray-700 truncate">
        {user?.firstName || user?.email?.split('@')[0]}
      </p>
      <p className="text-xs text-gray-500 truncate">
        {currentMembership
          ? <><span className="capitalize">{currentMembership.orgName}</span> · {roleLabel(currentMembership.role)}</>
          : user?.platformRole === 'super_admin' && roleLabel('admin')}
      </p>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SupportModeBanner />
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-primary-600">
                ZOS
              </h1>
            </div>
            {/* ≥ 1360 px : onglets avec libellés sur la ligne du logo (pas de défilement, il couperait les menus déroulants).
                640–1359 px (tablettes) : les mêmes onglets, libellés compris, sur une seconde ligne sous le logo (voir plus bas).
                < 640 px (téléphones) : bouton « Menu » + panneau latéral. */}
            <nav className="flex items-center gap-0.5 sm:gap-1 min-w-0 [&_a]:whitespace-nowrap">
              <div className="sm:hidden">
                <CompactNav
                  items={navItems}
                  groupLabel={t('nav_org')}
                  groupIcon={ICONS.organisation}
                  groupItems={orgItems}
                  footer={
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-primary-700 text-sm font-medium">
                          {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div className="min-w-0">{identity}</div>
                    </div>
                  }
                />
              </div>
              <div className="hidden min-[1360px]:flex items-center gap-1">
                {navItems.map(it => <NavItem key={it.to} {...it} />)}
                {orgItems.length > 0 && <NavGroup label={t('nav_org')} icon={ICONS.organisation} items={orgItems} />}
              </div>

              {/* Assistant ZOS (questions en langage naturel sur les données) */}
              {hasAccess('wattelzce') && <AssistantPanel />}

              {/* Langue */}
              <ThemeSwitcher />
              <LanguageSwitcher />

              {/* Alertes de supervision */}
              <AlertBell />

              {/* User menu */}
              <div className="flex items-center ml-1 pl-1 sm:ml-3 sm:pl-3 border-l border-gray-200 whitespace-nowrap">
                {/* Avatar masqué sur téléphone : l'identité est au pied du menu compact */}
                <div className="hidden sm:flex items-center">
                  <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-700 text-sm font-medium">
                      {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="ml-2 hidden 2xl:block">{identity}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-1 sm:ml-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title={t('nav_logout')}
                  aria-label={t('nav_logout')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </nav>
          </div>
          {/* Tablettes : seconde ligne d'onglets avec libellés, qui passe à la ligne si besoin (jamais de défilement). */}
          <nav className="hidden sm:flex min-[1360px]:hidden flex-wrap items-center gap-1 -mt-1 pb-2.5 [&_a]:whitespace-nowrap">
            {navItems.map(it => <NavItem key={it.to} {...it} />)}
            {orgItems.length > 0 && <NavGroup label={t('nav_org')} icon={ICONS.organisation} items={orgItems} />}
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 flex-shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-500">
            {t('sh_footer_copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
